import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";

const DatePickerHeader = ({ selectedDate, onDateChange, transactionDates = [] }) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));

  // Format date for display
  const formatDisplayDate = (date) => {
    // A "YYYY-MM-DD" string parses as UTC midnight, which shifts back a day in
    // negative-offset timezones (so "today" shows as "Yesterday"). Parse it as
    // local midnight instead.
    const d = typeof date === "string" ? new Date(date + "T00:00:00") : new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (d.toDateString() === today.toDateString()) {
      return "Today";
    } else if (d.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else if (d.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }
    
    return d.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
    });
  };

  // Navigate to previous/next day
  const navigateDay = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    onDateChange(newDate.toISOString().split('T')[0]);
  };

  // Jump to today
  const goToToday = () => {
    onDateChange(new Date().toISOString().split('T')[0]);
  };

  // Check if a date has transactions
  const hasTransactions = (dateStr) => {
    return transactionDates.some(d => d.date === dateStr);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    
    const days = [];
    
    // Previous month padding
    for (let i = startPadding - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const isSelected = (date) => {
    return date.toISOString().split('T')[0] === selectedDate;
  };

  const isToday = (date) => {
    return date.toDateString() === new Date().toDateString();
  };

  useEffect(() => {
    setCurrentMonth(new Date(selectedDate));
  }, [selectedDate]);

  return (
    <div className="relative">
      {/* Main Header */}
      <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl p-3 border border-slate-700">
        
        {/* Previous Day */}
        <button 
          onClick={() => navigateDay(-1)}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white"
        >
          <ChevronLeft size={20} />
        </button>

        {/* Date Display & Picker Toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCalendar(!showCalendar)}
            className="flex items-center gap-2 px-4 py-2 hover:bg-slate-700/50 rounded-lg transition-colors"
          >
            <Calendar size={18} className="text-indigo-400" />
            <span className="text-lg font-semibold text-white">
              {formatDisplayDate(selectedDate)}
            </span>
          </button>
          
          {selectedDate !== new Date().toISOString().split('T')[0] && (
            <button
              onClick={goToToday}
              className="text-xs text-indigo-400 hover:text-indigo-300 px-2 py-1 rounded bg-indigo-500/10 hover:bg-indigo-500/20 transition-colors"
            >
              Today
            </button>
          )}
        </div>

        {/* Next Day */}
        <button 
          onClick={() => navigateDay(1)}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar Dropdown */}
      {showCalendar && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 bg-slate-800 rounded-xl border border-slate-700 shadow-2xl p-4 animate-fade-in min-w-[300px]">
          
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="text-white font-medium">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} className="text-center text-xs text-slate-500 font-medium py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {generateCalendarDays().map(({ date, isCurrentMonth }, idx) => {
              const dateStr = date.toISOString().split('T')[0];
              const hasTxn = hasTransactions(dateStr);
              
              return (
                <button
                  key={idx}
                  onClick={() => {
                    onDateChange(dateStr);
                    setShowCalendar(false);
                  }}
                  className={`
                    relative p-2 text-sm rounded-lg transition-all
                    ${!isCurrentMonth ? 'text-slate-600' : 'text-slate-300'}
                    ${isSelected(date) ? 'bg-indigo-600 text-white font-bold' : 'hover:bg-slate-700'}
                    ${isToday(date) && !isSelected(date) ? 'ring-1 ring-indigo-500' : ''}
                  `}
                >
                  {date.getDate()}
                  {hasTxn && (
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-emerald-400" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-4 pt-3 border-t border-slate-700 text-xs text-slate-400">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
              <span>Has trades</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-4 h-4 rounded ring-1 ring-indigo-500" />
              <span>Today</span>
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      {showCalendar && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowCalendar(false)}
        />
      )}
    </div>
  );
};

export default DatePickerHeader;
